// src/components/scene/UploadedModel.tsx
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useThree } from '@react-three/fiber';
import { PerspectiveCamera, PropertyBinding } from 'three';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { DEFAULT_LAYER_COLOR, LAYER_HIGHLIGHT_LEGACY, LAYER_HIGHLIGHT_V2 } from '../../../constants/colors.ts';
import { useAppDispatch } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import { setCameraInitialized, setUseOriginalTextures } from '../../../store/reducers/workspace.reducer.ts';
import { getUserFeatureFlags } from '../../../store/selectors/featureFlags.selector';
import { getNorms } from '../../../store/selectors/user.selector';
import { RootState } from '../../../store/store.ts';
import { centerCameraOnModel } from '../../../utils/cameraUtils';
import { WORKSPACE_LAYOUT_V2_FLAG } from '../../../utils/featureFlagKeys';
import { getLayerDisplayColor } from '../../../utils/layerColorUtils.ts';
import { cloneParsedModel, loadParsedModel } from '../../../utils/modelLoadCache';
import { requirementFromMesh } from '../../../utils/photometricTargets';
import { hasOriginalTextures } from '../../../utils/threeUtils';

type ModelType = {
  name: string;
  url: string;
};

// A transformation matrix to swap the Y and Z axes. This is useful for converting
// from a Z-up coordinate system to the default Y-up system used in three.js.
// It maps: (x, y, z) -> (x, -z, y).
const transformMatrix = new THREE.Matrix4();
transformMatrix.set(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

export const UploadedModel = ({ model, onLoad }: { model: ModelType; onLoad?: () => void }) => {
  const {
    selectedLayer,
    hoveredLayer,
    selectedObject,
    setSelectedObject,
    setHoveredLayer,
    activeViewModes,
    project,
    activeVersion,
    modelLayers,
    setModelLayers,
    setSelectedLayer,
    handleLayerClick,
  } = useStore();
  const norms = useSelector(getNorms);
  const useOriginalTextures = useSelector((state: RootState) => state.workspace.useOriginalTextures);
  const layoutV2 = useSelector((state: RootState) => getUserFeatureFlags(state).includes(WORKSPACE_LAYOUT_V2_FLAG));
  const dispatch = useAppDispatch();
  const [loadedObject, setLoadedObject] = useState<THREE.Object3D | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { camera, controls } = useThree((state) => ({
    camera: state.camera as PerspectiveCamera,
    controls: state.controls as OrbitControls,
  }));

  useEffect(() => {
    if (hasLoaded && loadedObject) return;
    const loadModel = async () => {
      let extension = model.name.split('.').pop()?.toLowerCase();
      if (!extension || extension === model.name.toLowerCase()) {
        const urlParts = model.url.split('?')[0].split('.');
        if (urlParts.length > 1) {
          extension = urlParts.pop()?.toLowerCase();
        }
      }
      if (extension !== 'glb' && extension !== 'gltf' && extension !== 'obj') {
        console.error(`Unsupported file format: ${extension} for file ${model.name}`);
        return;
      }
      try {
        // Parse once per URL and reuse across views (the measurement view clones
        // the same cache entry); clone here for our own material/transform edits.
        const { object, originalNames: originalNamesMap } = cloneParsedModel(
          await loadParsedModel(model.url, extension),
        );

        const layers: {
          uuid: string;
          groupId?: string | null;
          threeJsName: string;
          originalName: string;
          displayName: string;
          color?: string;
          modelName: string;
          visible: boolean;
          isIlluminated: boolean;
          canHoldLuminaire: boolean;
          layerType: string;
          selectedNorm: null | string;
          customTargets?: ReturnType<typeof requirementFromMesh>['customTargets'];
        }[] = [];

        const meshes = activeVersion?.meshes || [];
        const meshObjectsMap = new Map<string, THREE.Mesh>();
        let counter = 0;
        object.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            if (extension === 'glb' || extension === 'gltf') {
              child.applyMatrix4(transformMatrix);
            }
            // Must match the saga path (handleSwitchActiveVersion) which keys layers via
            // PropertyBinding.sanitizeNodeName. Otherwise mesh.userData.layerName diverges
            // from Redux layer.threeJsName after a version switch for any mesh whose raw
            // name contains '.', ' ', ':', '/', '[', ']' or '\\'.
            const threeJsName = PropertyBinding.sanitizeNodeName(child.name || `Mesh_${counter}`);
            const originalName = originalNamesMap.get(counter);
            if (originalName) meshObjectsMap.set(originalName, child);
            if (child.material) {
              child.userData.originalMaterial = Array.isArray(child.material)
                ? child.material.map((m) => m.clone())
                : child.material.clone();
            }
            child.userData.layerName = threeJsName;
            child.userData.originalName = originalName || threeJsName;
            child.userData.interactive = true;
            child.userData.onClick = () => {
              handleLayerClick(threeJsName, originalName || threeJsName);
            };
            counter++;
          }
        });
        meshes.forEach((backendMesh) => {
          const meshObject = meshObjectsMap.get(backendMesh.name);
          let layerType = 'Unspecified';
          if (backendMesh.type) {
            layerType = backendMesh.type.charAt(0).toUpperCase() + backendMesh.type.slice(1).toLowerCase();
          }
          const requirement = requirementFromMesh(backendMesh, norms);
          const threeJsName = PropertyBinding.sanitizeNodeName(
            meshObject ? meshObject.name || backendMesh.name : backendMesh.name,
          );
          layers.push({
            uuid: backendMesh.uuid,
            groupId: backendMesh.groupId,
            threeJsName: threeJsName,
            originalName: backendMesh.name,
            displayName: backendMesh.displayName || backendMesh.name,
            color: backendMesh.color,
            modelName: model.name,
            visible: true,
            isIlluminated: requirement.isIlluminated,
            canHoldLuminaire: backendMesh.canHoldLight,
            layerType: layerType,
            selectedNorm: requirement.selectedNorm,
            customTargets: requirement.customTargets,
          });
          if (meshObject) {
            meshObject.userData.layerName = threeJsName;
            meshObject.userData.onClick = () => {
              handleLayerClick(threeJsName, backendMesh.name);
            };
          }
        });
        setModelLayers(layers);
        setLoadedObject(object);

        const modelHasTextures = hasOriginalTextures(object);
        dispatch(setUseOriginalTextures(modelHasTextures));

        setIsLoading(false);
        setHasLoaded(true);
        if (onLoad) onLoad();
      } catch (error) {
        console.error('Error loading model:', error);
        setIsLoading(false);
      }
    };
    loadModel();
  }, [
    model,
    setModelLayers,
    setSelectedLayer,
    setSelectedObject,
    selectedLayer,
    setHoveredLayer,
    hasLoaded,
    loadedObject,
    project?.versions,
    project?.activeVersionId,
    activeVersion?.meshes,
    norms,
    handleLayerClick,
    onLoad,
    dispatch,
  ]);

  const cameraInitialized = useSelector((state: RootState) => state.workspace.cameraInitialized);

  useEffect(() => {
    if (!loadedObject) return;
    if (!cameraInitialized) {
      centerCameraOnModel(loadedObject, camera, controls);
      dispatch(setCameraInitialized(true));
    }
  }, [loadedObject, camera, controls, cameraInitialized, dispatch]);

  useEffect(() => {
    if (!loadedObject) return;
    loadedObject.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.Mesh) {
        const isSelected = selectedObject === child.uuid || selectedLayer === child.userData.layerName;
        const isHovered = hoveredLayer === child.userData.layerName;
        const correspondingLayer = modelLayers.find((layer) => layer.threeJsName === child.userData.layerName);

        // ── Mesh fill color ──────────────────────────────────────────────
        // Color is driven by CHL/LMN state; default is gray.
        // Hover/select do NOT change mesh color — only borders change.
        const hasChlOrLmn = correspondingLayer?.canHoldLuminaire || correspondingLayer?.isIlluminated;
        const fillColor = correspondingLayer ? getLayerDisplayColor(correspondingLayer) : DEFAULT_LAYER_COLOR;

        const palette = layoutV2 ? LAYER_HIGHLIGHT_V2 : LAYER_HIGHLIGHT_LEGACY;

        // Priority: 1. hover  2. texture  3. CHL/LMN color  4. selected  5. grey
        if (isHovered && palette.hoverFill) {
          child.material = new THREE.MeshStandardMaterial({
            color: palette.hoverFill,
            emissive: palette.hoverFill,
            emissiveIntensity: 0.5,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.8,
          });
        } else if (useOriginalTextures) {
          child.material = child.userData.originalMaterial;
        } else if (hasChlOrLmn) {
          child.material = new THREE.MeshStandardMaterial({
            color: fillColor,
            emissive: fillColor,
            emissiveIntensity: 0.5,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.95,
          });
        } else if (isSelected) {
          child.material = new THREE.MeshStandardMaterial({
            color: palette.selectedFill,
            emissive: palette.selectedFill,
            emissiveIntensity: 0.4,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.85,
          });
        } else {
          child.material = new THREE.MeshStandardMaterial({
            color: fillColor,
            emissive: fillColor,
            emissiveIntensity: 0.15,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.95,
          });
        }

        // ── Edge borders (multiple passes for thickness) ────────────────
        const toRemove = child.children.filter((c) => c.userData.isEdgeLine);
        toRemove.forEach((c) => child.remove(c));

        let borderColor = palette.border;
        let borderOpacity = 0.8;
        if (isSelected) {
          borderColor = palette.borderSelected;
          borderOpacity = 1.0;
        } else if (isHovered) {
          borderColor = palette.borderHovered;
          borderOpacity = 0.9;
        }

        const edges = new THREE.EdgesGeometry(child.geometry, 30);
        // Render multiple passes at offsets to simulate thick borders
        const offsets = [
          0, 0.004, -0.004, 0.008, -0.008, 0.012, -0.012, 0.016, -0.016, 0.002, -0.002, 0.006, -0.006, 0.01, -0.01,
          0.014, -0.014, 0.018, -0.018, 0.02, -0.02,
        ];
        offsets.forEach((offset, i) => {
          const edgeMat = new THREE.LineBasicMaterial({
            color: borderColor,
            transparent: true,
            opacity: i === 0 ? borderOpacity : borderOpacity * 0.7,
          });
          const lineSegments = new THREE.LineSegments(edges, edgeMat);
          lineSegments.position.set(offset, offset, offset);
          lineSegments.userData.isEdgeLine = true;
          lineSegments.raycast = () => {};
          child.add(lineSegments);
        });
      }
    });
  }, [
    selectedLayer,
    hoveredLayer,
    selectedObject,
    loadedObject,
    activeViewModes,
    useOriginalTextures,
    modelLayers,
    layoutV2,
  ]);

  if (isLoading) {
    return (
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#B3EC49" wireframe={true} />
      </mesh>
    );
  }

  if (!loadedObject) return null;

  return <primitive object={loadedObject} position={[0, 0, 0]} />;
};
